import { useState } from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import CloseIcon from "@mui/icons-material/Close";
import CropIcon from "@mui/icons-material/Crop";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import CheckIcon from "@mui/icons-material/Check";
import { colors } from "../../../../data/colors";
import { customViewerStyles } from "../../../../data/styles/modalStyles";
import BasicButton from "../../../BasicButton";
import {
  fontFamilies,
  useImageEditor,
} from "./useImageEditor";

function AdjustSlider({
  label,
  min,
  max,
  value,
  onChange,
  onCommit,
}: {
  label: string;
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
  onCommit: () => void;
}) {
  // Ranges of [-1, 1] represent a fractional factor rather than a count,
  // so show them as a percentage (-1 -> 0%, 0 -> 50%, 1 -> 100%) instead
  // of the raw fraction.
  const isUnitRange = min === -1 && max === 1;
  const step = max - min <= 2 ? 0.01 : 1;
  const displayValue = isUnitRange
    ? `${Math.round(((value - min) / (max - min)) * 100)}%`
    : Math.round(value * 100) / 100;

  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 12,
          marginBottom: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>{label}</span>
        <span>{displayValue}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onMouseUp={onCommit}
        onTouchEnd={onCommit}
        style={{ width: "100%" }}
      />
    </div>
  );
}

export default function ImageEditorModal({
  open,
  imageSources,
  onClose,
  onSave,
}: {
  open: boolean;
  imageSources: string[];
  onClose: () => void;
  onSave: (dataUrl: string) => void;
}) {
  const { t } = useTranslation();
  const {
    canvasElRef,
    isReady,
    loadError,
    canUndo,
    canRedo,
    hasChanges,
    isCropping,
    isBusy,
    adjustments,
    selectedText,
    undo,
    redo,
    applyAdjustments,
    addText,
    updateSelectedTextStyle,
    startCrop,
    applyCrop,
    cancelCrop,
    runRemoveBackground,
    exportImage,
  } = useImageEditor(imageSources, open);

  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);

  const askClose = () => {
    if (hasChanges) {
      setConfirmCancel(true);
    } else {
      onClose();
    }
  };

  const askSave = () => {
    setConfirmSave(true);
  };

  const doSave = () => {
    const dataUrl = exportImage();
    setConfirmSave(false);
    if (dataUrl) {
      onSave(dataUrl);
    }
  };

  const toolbarColor = colors.analitics;

  return (
    <Modal
      isOpen={open}
      onRequestClose={askClose}
      style={customViewerStyles({ z1: 70, z2: 71 })}
    >
      <div className="documentContainer">
        <div className="documentBar" style={{ backgroundColor: toolbarColor }}>
          <div style={{ flex: 1 }}>{t("p.editImage")}</div>
          <UndoIcon
            onClick={() => canUndo && undo()}
            style={{
              cursor: canUndo ? "pointer" : "default",
              opacity: canUndo ? 1 : 0.4,
            }}
          />
          <RedoIcon
            onClick={() => canRedo && redo()}
            style={{
              cursor: canRedo ? "pointer" : "default",
              opacity: canRedo ? 1 : 0.4,
              marginLeft: 8,
            }}
          />
          <CloseIcon
            onClick={askClose}
            style={{ cursor: "pointer", marginLeft: 12 }}
          />
        </div>
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <div
            style={{
              flex: 1,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f1f1f1",
              padding: 10,
            }}
          >
            <canvas
              ref={canvasElRef}
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
            {(!isReady || isBusy || loadError) && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.7)",
                }}
              >
                {loadError ? (
                  <div style={{ color: colors.red, textAlign: "center" }}>
                    {t("p.errorLoadingImage")}
                  </div>
                ) : (
                  <>
                    <div
                      className="spinner"
                      style={{ width: 40, height: 40 }}
                    />
                    {isBusy && (
                      <div style={{ marginTop: 10 }}>
                        {t("p.removingBackground")}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div
            style={{
              width: 260,
              padding: 10,
              overflowY: "auto",
              borderLeft: `solid 2px ${colors.lightGrey}`,
            }}
          >
            {isCropping ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <BasicButton
                  title={t("p.applyCrop")}
                  Icon={CheckIcon}
                  color={colors.green}
                  onClick={applyCrop}
                />
                <BasicButton
                  title={t("p.cancel")}
                  color={colors.darkGrey}
                  onClick={cancelCrop}
                />
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <BasicButton
                    title={t("p.crop")}
                    Icon={CropIcon}
                    color={colors.blue}
                    onClick={startCrop}
                  />
                  <BasicButton
                    title={t("p.removeBackground")}
                    Icon={AutoFixHighIcon}
                    color={colors.violet}
                    onClick={() => runRemoveBackground()}
                  />
                  <BasicButton
                    title={t("p.addText")}
                    Icon={TextFieldsIcon}
                    color={colors.orange}
                    onClick={addText}
                  />
                </div>

                <div style={{ marginTop: 15 }}>
                  <AdjustSlider
                    label={t("p.contrast")}
                    min={-1}
                    max={1}
                    value={adjustments.contrast}
                    onChange={(v) =>
                      applyAdjustments({ ...adjustments, contrast: v }, false)
                    }
                    onCommit={() => applyAdjustments(adjustments, true)}
                  />
                  <AdjustSlider
                    label={t("p.brightness")}
                    min={-1}
                    max={1}
                    value={adjustments.brightness}
                    onChange={(v) =>
                      applyAdjustments(
                        { ...adjustments, brightness: v },
                        false,
                      )
                    }
                    onCommit={() => applyAdjustments(adjustments, true)}
                  />
                  <AdjustSlider
                    label={t("p.saturation")}
                    min={-1}
                    max={1}
                    value={adjustments.saturation}
                    onChange={(v) =>
                      applyAdjustments(
                        { ...adjustments, saturation: v },
                        false,
                      )
                    }
                    onCommit={() => applyAdjustments(adjustments, true)}
                  />
                  <AdjustSlider
                    label={t("p.hue")}
                    min={-180}
                    max={180}
                    value={adjustments.hue}
                    onChange={(v) =>
                      applyAdjustments({ ...adjustments, hue: v }, false)
                    }
                    onCommit={() => applyAdjustments(adjustments, true)}
                  />
                  <div style={{ fontSize: 13, fontWeight: "bold", margin: "8px 0" }}>
                    {t("p.levels")}
                  </div>
                  <AdjustSlider
                    label={t("p.blackPoint")}
                    min={0}
                    max={254}
                    value={adjustments.blackPoint}
                    onChange={(v) =>
                      applyAdjustments(
                        {
                          ...adjustments,
                          blackPoint: Math.min(v, adjustments.whitePoint - 1),
                        },
                        false,
                      )
                    }
                    onCommit={() => applyAdjustments(adjustments, true)}
                  />
                  <AdjustSlider
                    label={t("p.whitePoint")}
                    min={1}
                    max={255}
                    value={adjustments.whitePoint}
                    onChange={(v) =>
                      applyAdjustments(
                        {
                          ...adjustments,
                          whitePoint: Math.max(v, adjustments.blackPoint + 1),
                        },
                        false,
                      )
                    }
                    onCommit={() => applyAdjustments(adjustments, true)}
                  />
                </div>

                {selectedText && (
                  <div
                    style={{
                      marginTop: 15,
                      paddingTop: 10,
                      borderTop: `solid 1px ${colors.lightGrey}`,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: "bold", marginBottom: 6 }}>
                      {t("p.text")}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, marginBottom: 2 }}>
                        {t("p.fontFamily")}
                      </div>
                      <select
                        value={selectedText.fontFamily}
                        onChange={(e) =>
                          updateSelectedTextStyle({
                            fontFamily: e.target.value,
                          })
                        }
                        style={{ width: "100%" }}
                      >
                        {fontFamilies.map((f) => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, marginBottom: 2 }}>
                        {t("p.fontSize")}
                      </div>
                      <input
                        type="number"
                        min={8}
                        max={200}
                        value={selectedText.fontSize}
                        onChange={(e) =>
                          updateSelectedTextStyle({
                            fontSize: Number(e.target.value),
                          })
                        }
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, marginBottom: 2 }}>
                        {t("p.color")}
                      </div>
                      <input
                        type="color"
                        value={selectedText.fill}
                        onChange={(e) =>
                          updateSelectedTextStyle({ fill: e.target.value })
                        }
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <BasicButton
                        Icon={FormatBoldIcon}
                        color={
                          selectedText.fontWeight === "bold"
                            ? colors.blue
                            : colors.grey
                        }
                        onClick={() =>
                          updateSelectedTextStyle({
                            fontWeight:
                              selectedText.fontWeight === "bold"
                                ? "normal"
                                : "bold",
                          })
                        }
                      />
                      <BasicButton
                        Icon={FormatItalicIcon}
                        color={
                          selectedText.fontStyle === "italic"
                            ? colors.blue
                            : colors.grey
                        }
                        onClick={() =>
                          updateSelectedTextStyle({
                            fontStyle:
                              selectedText.fontStyle === "italic"
                                ? "normal"
                                : "italic",
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 8,
            padding: 10,
            justifyContent: "center",
            borderTop: `solid 2px ${colors.lightGrey}`,
          }}
        >
          <BasicButton
            title={t("p.save")}
            color={colors.green}
            onClick={askSave}
          />
          <BasicButton
            title={t("p.cancel")}
            color={colors.darkGrey}
            onClick={askClose}
          />
        </div>
      </div>

      {confirmCancel && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <div
            style={{
              background: colors.white,
              padding: 20,
              borderRadius: 5,
              maxWidth: 320,
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: 10 }}>
              {t("p.discardChanges")}
            </div>
            <div style={{ marginBottom: 15 }}>
              {t("p.discardChangesMessage")}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <BasicButton
                title={t("p.discardChanges")}
                color={colors.red}
                onClick={() => {
                  setConfirmCancel(false);
                  onClose();
                }}
              />
              <BasicButton
                title={t("p.cancel")}
                color={colors.darkGrey}
                onClick={() => setConfirmCancel(false)}
              />
            </div>
          </div>
        </div>
      )}

      {confirmSave && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <div
            style={{
              background: colors.white,
              padding: 20,
              borderRadius: 5,
              maxWidth: 320,
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: 10 }}>
              {t("p.saveImageChanges")}
            </div>
            <div style={{ marginBottom: 15 }}>
              {t("p.saveImageChangesMessage")}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <BasicButton
                title={t("p.save")}
                color={colors.green}
                onClick={doSave}
              />
              <BasicButton
                title={t("p.cancel")}
                color={colors.darkGrey}
                onClick={() => setConfirmSave(false)}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
